import { get, ref } from "firebase/database";
import { APP_LOGO, APP_NAME } from "./config";
import { auth, database } from "./firebase";
import {
  signInWithEmailAndPassword,
  signOut,
  sendEmailVerification,
  sendPasswordResetEmail,
} from "firebase/auth";
import { useContext, useEffect, useState } from "react";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "./AuthContext";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  // Pre-fill email if it was saved in cookies
  useEffect(() => {
    const savedEmail = Cookies.get(`${APP_NAME}_email`);
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  // LOGIN handler
  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      if (!user.emailVerified) {
        await sendEmailVerification(user);
        setError(
          "Your email is not verified. A new verification link has been sent."
        );
        await signOut(auth);
        return;
      }

      const snapshot = await get(ref(database, "helpdesk/users/" + user.uid));
      if (snapshot.exists()) {
        const dbUser = { uid: user.uid, ...snapshot.val() };

        // Update context and cookies
        login(dbUser, rememberMe);

        navigate("/dashboard", { replace: true });
      } else {
        setError("No user profile found in database.");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // FORGOT PASSWORD
  const handleForgotPassword = async () => {
    navigate("/forgot-password");
  };

  return (
    <form onSubmit={handleLogin}>
      {/* Email */}
      <div className="form-group">
        <div className="input-group mb-4">
          <div className="input-group-prepend">
            <span className="input-group-text">
              <i className="fa fa-user text-gray-400"></i>
            </span>
          </div>
          <input
            type="text"
            className="form-control"
            placeholder="Email or Username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
      </div>

      {/* Password */}
      <div className="form-group">
        <div className="input-group mb-4">
          <div className="input-group-prepend">
            <span className="input-group-text">
              <i className="fa fa-lock text-gray-400"></i>
            </span>
          </div>
          <input
            type="password"
            className="form-control"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
      </div>

      {/* Error message */}
      {error && <div className="text-danger mb-3">{error}</div>}

      {/* Submit button */}
      <button
        type="submit"
        className="btn btn-primary btn-block mb-3"
        disabled={loading}
      >
        {loading ? "Logging in..." : "Login"}
      </button>

      <hr />

      {/* Remember me & Forgot password */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            id="rememberMe"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            style={{ width: "24px", height: "24px", cursor: "pointer" }}
          />
          <label
            className="form-check-label"
            htmlFor="rememberMe"
            style={{ fontSize: "1.2rem", marginLeft: "8px", cursor: "pointer" }}
          >
            Remember me
          </label>
        </div>

        <button
          type="button"
          className="btn btn-link p-0"
          onClick={handleForgotPassword}
        >
          Forgot password?
        </button>
      </div>
    </form>
  );
};

export default Login;
